import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useAuth from '../hooks/auth/useAuth';
import usePermissions, { ManagerRole } from '../hooks/auth/usePermissions';
import usePlgOnboarding from '../hooks/auth/usePlgOnboarding';
import { shouldShowIntercom } from '../services/tenant/intercomVisibility';

const STORE_ROLES = [ManagerRole.StoreAdmin, ManagerRole.StoreEmployee, ManagerRole.StoreOperator];

// Module-level, not refs: InitialDataProvider swaps its children for a loading
// screen on every company load, so the widget remounts constantly while the
// Intercom session itself lives on the page. Shutting down on unmount would
// flash the launcher on each restaurant switch.
const session = { loaded: false, booted: false };

/** Test-only: forget the page-level session between isolated renders. */
export function resetIntercomSession() {
  session.loaded = false;
  session.booted = false;
}

export default function IntercomWidget() {
  const { company } = useSelector((state) => state.currentCompany);
  const { franchise } = useSelector((state) => state.currentFranchise);
  const { profile } = useSelector((state) => state.initialData);

  const { isLoggedIn } = useAuth();
  const { primaryRole, hasGlobalAccess, isSuperAdmin, hasAnyRole } = usePermissions();
  const isPlgPartner = usePlgOnboarding();

  // Who gets it is the provider's `messenger_audience`: everyone under
  // FoodAmigos, only the admins of a white-label partner (for the FIN agent),
  // nobody under a legacy reseller. See intercomVisibility.
  const visible = shouldShowIntercom({
    appId: import.meta.env.VITE_INTERCOM_APP_ID,
    isLoggedIn,
    isSuperAdmin,
    hasGlobalAccess,
    isStoreUser: hasAnyRole(STORE_ROLES, false),
    isAdmin: hasAnyRole([ManagerRole.Admin], false),
    ownProvider: profile?.service_provider,
    company,
    franchise,
  });

  // `usePlgOnboarding` is tenant-shaped, so it is also true for FoodAmigos staff
  // acting inside a partner tenant. Those are not partners and stay out of the
  // segment — the same global-access staff bypass HasMasterProviderGuard
  // applies.
  const isPartner = isPlgPartner && !hasGlobalAccess;

  const profileProps = {
    user_id: typeof profile?.id === 'string' ? profile.id.trim() : null,
    // Partner staff and admins often have no restaurant selected, which used to
    // read as "Name (undefined)" in Intercom.
    name: company?.name ? `${profile?.name} (${company.name})` : profile?.name,
    email: profile?.email,
    created_at: moment(profile?.created_at ?? undefined).unix(),
    company: company?.name,
    // Single value, not the full `profile.roles` list: Intercom custom
    // attributes cannot hold an array, and audience segments filter on
    // equality. Re-sent on every initialise, so a role change lands with the
    // user's next session.
    user_role: primaryRole,
    // The role alone cannot carry this: a partner's own admin holds plain
    // `admin`, indistinguishable from a FoodAmigos admin. Partner is the tenant,
    // so segments targeting partners filter on this, not on user_role.
    is_partner: isPartner,
  };

  const props = {
    app_id: import.meta.env.VITE_INTERCOM_APP_ID,
    ...(profile?.id ? profileProps : {}),
  };

  // Visibility follows the restaurant in context, so it can flip within a
  // session (switching restaurants, logging out). The SDK itself never takes
  // the launcher down, hence the explicit shutdown; after one, `boot` brings it
  // back. `Intercom()` is only called once visible, so the script is never
  // loaded — and no contact identified — for a session that never qualifies.
  const propsKey = JSON.stringify(props);

  useEffect(() => {
    if (!visible) {
      if (session.booted) {
        shutdown();
        session.booted = false;
      }

      return;
    }

    if (!session.loaded) {
      Intercom(props);
      session.loaded = true;
    } else if (!session.booted) {
      boot(props);
    } else {
      update(props);
    }

    session.booted = true;
    // Keyed on the serialized payload, not the object rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, propsKey]);

  return null;
}
